import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission, 
  subscribeToPush 
} from '@/services/pushNotifications';

export function NotificationPrompt({ userId, userType, onClose }) {
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed
    const wasDismissed = localStorage.getItem('notification_prompt_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }

    // Check current permission
    if (isPushSupported()) {
      setPermission(getNotificationPermission());
    }
  }, []);

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      const result = await requestNotificationPermission();
      setPermission(result);
      
      if (result === 'granted' && userId) {
        await subscribeToPush(userId, userType);
      }
      
      if (result !== 'default') {
        handleDismiss();
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('notification_prompt_dismissed', 'true');
    setDismissed(true);
    onClose?.();
  };

  // Don't show if not supported, already granted, denied, or dismissed
  if (!isPushSupported() || permission === 'granted' || permission === 'denied' || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-card border border-border rounded-xl shadow-lg p-4 z-50 animate-in slide-in-from-bottom-4">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full"
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-foreground">Enable Notifications</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Get instant updates about your rides, driver arrivals, and more.
          </p>
          <div className="flex gap-2 mt-3">
            <Button 
              size="sm" 
              onClick={handleEnableNotifications}
              disabled={loading}
            >
              {loading ? 'Enabling...' : 'Enable'}
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleDismiss}
            >
              Not Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationPrompt;
